import { motion } from "framer-motion";
import { WEDDING } from "@/lib/wedding";
import SmartImage from "@/components/SmartImage.jsx";

const v = WEDDING.venue;

/**
 * Venue
 * -----------------------------------------------------------
 * Photo, notes, stay note and an embedded map, all from wedding.js.
 */
// Matching outline buttons for the "Getting there" card
const BUTTON =
  "flex items-center justify-center text-center px-4 py-3 border border-rani text-rani rounded-full text-[11px] uppercase tracking-chip font-semibold transition-colors hover:bg-rani hover:text-ivory";

export default function Venue() {
  return (
    <section id="venue" className="relative edge py-24 text-center" style={{ background: "linear-gradient(180deg, #FAF6EE 0%, #F1E9D8 14%, #F1E9D8 86%, #FAF6EE 100%)" }}>
      <div className="overline text-rani mb-5">The Venue</div>
      <h2 className="font-display text-4xl sm:text-6xl text-rani">{v.name}</h2>
      <p className="mt-4 font-display italic text-xl text-ink/70">{v.address}</p>
      <div
        className="mx-auto mt-6 mb-14 h-px w-40"
        style={{ background: "linear-gradient(90deg, rgba(201,169,97,0) 0%, rgba(201,169,97,0.7) 50%, rgba(201,169,97,0) 100%)" }}
      />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8 text-left"
      >
        {/* Row 1: photo and map, identical frames */}
        <SmartImage
          src={v.image}
          alt={v.name}
          label={v.name}
          aspect="4/5"
          className="rounded-2xl border border-gold"
        />
        <div className="relative w-full aspect-[4/5] rounded-2xl border border-gold overflow-hidden bg-bone">
          <iframe
            src={v.mapEmbedUrl}
            title={`Map of ${v.name}`}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        </div>

        {/* Row 2: stay note and directions, identical cards */}
        <div className="h-full border border-gold/60 bg-ivory rounded-xl px-5 py-4">
          <div className="text-[11px] uppercase tracking-chip text-gold-deep mb-1.5">Your stay</div>
          <p className="font-display text-lg text-rani leading-snug">{v.stayNote}</p>
          {v.notes?.length > 0 && (
            <ul className="mt-3 grid gap-1.5">
              {v.notes.map((n) => (
                <li key={n} className="text-sm text-ink/80">{n}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="h-full border border-gold/60 bg-ivory rounded-xl px-5 py-4 flex flex-col">
          <div className="text-[11px] uppercase tracking-chip text-gold-deep mb-1.5">Getting there</div>
          <p className="font-display text-lg text-rani leading-snug">{v.name}, {v.address}</p>
          <div className="mt-auto pt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <a
              href={v.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={BUTTON}
            >
              Google Maps
            </a>
            {v.website && (
              <a
                href={v.website}
                target="_blank"
                rel="noopener noreferrer"
                className={BUTTON}
              >
                Venue website
              </a>
            )}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
